import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Lock, Truck } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../state/auth';
import { useCart } from '../state/cart';
import ShippingAddressFields from '../components/ShippingAddressFields';
import PayFirstProof from '../components/PayFirstProof';
import { trackInitiateCheckout, trackCompleteRegistration, getFbCookies } from '../lib/pixel';
import { trackEvent } from '../lib/track';
import { loadLeadContact } from '../lib/leadContact';
import { useT, useLocalField } from '../i18n';

const EMPTY_SHIPPING = {
  fullName: '', phone: '', city: '', neighborhood: '',
  address: '', landmark: '', postalCode: '',
};

const variantIcons = { softcover: '📖', hardcover: '📕', digital: '📱' };

// Pay-first multi-item checkout: pay for every book slot in the cart at once.
// No stories exist yet — they're created afterwards from the paid order. Mirrors
// the single-story Checkout page's account bootstrap + Stripe redirect, but posts
// the whole slots list to /api/checkout/create-cart-session.
export default function CheckoutCart() {
  const t = useT();
  const lf = useLocalField();
  const navigate = useNavigate();
  const { user, applyToken, isStaff } = useAuth();
  const { slots } = useCart();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deliveryCfg, setDeliveryCfg] = useState({ amountCents: 0 });
  const [email, setEmail] = useState(() => user?.email || loadLeadContact()?.email || '');
  const [ship, setShip] = useState(() => {
    const lead = loadLeadContact();
    return { ...EMPTY_SHIPPING, fullName: lead?.name || '', phone: lead?.phone || '' };
  });

  useEffect(() => { trackEvent('payfirst_checkout_view'); }, []);
  useEffect(() => {
    api.get('/api/site-config').then((c) => setDeliveryCfg(c?.delivery || { amountCents: 0 })).catch(() => {});
  }, []);
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user, email]);

  // An empty cart has nothing to pay for — send them back to browse.
  if (!slots || slots.length === 0) {
    return (
      <div className="cart-page">
        <h1 className="page-title">{t.checkoutTitle}</h1>
        <div className="form-zone" style={{ textAlign: 'center' }}>
          <p className="empty-state">{t.cartEmpty}</p>
          <Link to="/" className="submit-btn">{t.backToHome}</Link>
        </div>
      </div>
    );
  }

  const variantLabels = { softcover: t.softcover, hardcover: t.hardcover, digital: t.digital };
  const anyPhysical = slots.some((s) => s.variantType === 'softcover' || s.variantType === 'hardcover');
  const subtotalCents = slots.reduce((sum, s) => sum + (s.priceCents || 0), 0);
  // Delivery is a single flat fee for the whole order when any book ships. The
  // exact amount is confirmed server-side; this mirrors it for the summary.
  const deliveryCents = anyPhysical ? (deliveryCfg?.amountCents || 0) : 0;
  const orderTotalCents = subtotalCents + deliveryCents;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    trackEvent('payfirst_checkout_submit', { name: String(slots.length) });
    try {
      const body = {
        items: slots.map((s) => ({ templateSlug: s.templateSlug, variantSku: s.variantSku })),
        email,
        shippingAddress: anyPhysical ? { ...ship, country: 'إسرائيل' } : {},
        ...getFbCookies(),
        eventSourceUrl: window.location.href,
      };
      const resp = await api.post('/api/checkout/create-cart-session', body);
      // Brand-new email → the server created an account and returned a session
      // token; log the browser in so they land on the order already authenticated
      // and can create their stories right away.
      if (resp.loginToken) {
        try { await applyToken(resp.loginToken); } catch { /* token is still stored */ }
      }
      // No AddToCart here: in the pay-first flow the real AddToCart fired at the
      // add-to-cart click on the product page (see TemplateDetail) — firing a
      // second one at checkout would double-count the campaign's key metric.
      trackInitiateCheckout({ value: orderTotalCents / 100, eventId: resp.icEventId });
      // Pay-first has no earlier lead step, so CompleteRegistration fires here with
      // InitiateCheckout. The server sends the reliable CAPI copy (deduped by
      // crEventId) since this browser event often dies in the Stripe redirect.
      trackCompleteRegistration({ value: orderTotalCents / 100, eventId: resp.crEventId });
      window.location.href = resp.sessionUrl;
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="checkout-page">
      <form className="checkout-form" onSubmit={handleSubmit}>
        <h2>{t.checkoutTitle}</h2>
        <p className="cart-pending-hint">{t.payFirstCheckoutHint}</p>

        <label className="field-label">{t.email}</label>
        <input type="email" name="email" autoComplete="email" className="field-input" value={email}
          onChange={e => setEmail(e.target.value)} required />

        {anyPhysical && (
          <ShippingAddressFields value={ship} onChange={setShip} />
        )}

        {error && <p className="error-text">{error}</p>}

        {isStaff && (
          <p className="admin-free-notice">{t.adminFreeNotice}</p>
        )}

        <div className="payfirst-reassure">
          <ShieldCheck size={20} aria-hidden="true" />
          <span>{t.payFirstReassurance}</span>
        </div>

        {/* Social proof at the moment of hesitation — a real customer's
            conversation video + the WhatsApp screenshots, right before the
            pay button. */}
        <PayFirstProof showShots />

        {anyPhysical && (
          <div className="checkout-delivery-estimate">
            <Truck size={18} aria-hidden="true" />
            <span>{t.checkoutDeliveryEstimate}</span>
          </div>
        )}

        <button type="submit" className="submit-btn" disabled={submitting}>
          {submitting ? t.checkoutProcessing : (isStaff ? t.adminFreeBtn : t.checkoutPay)}
        </button>

        <div className="checkout-trust">
          <div className="checkout-trust-item">
            <Lock size={16} />
            <span>{t.checkoutTrustSsl}</span>
          </div>
          <div className="checkout-trust-item">
            <ShieldCheck size={16} />
            <span>{t.checkoutTrustStripe}</span>
          </div>
        </div>
      </form>

      <div className="checkout-summary">
        <h3>{t.checkoutOrderSummary}</h3>
        {slots.map((s) => (
          <div key={s.id} className="checkout-summary-item">
            <span>{variantIcons[s.variantType] || '📖'} {lf(s, 'nameAr')}</span>
            <span className="checkout-summary-variant">{variantLabels[s.variantType] || s.variantType}</span>
            <span className="checkout-summary-price">{((s.priceCents || 0) / 100).toFixed(0)}{t.currency}</span>
          </div>
        ))}
        <div className="checkout-summary-row">
          <span>{t.subtotal}</span>
          <span>{(subtotalCents / 100).toFixed(0)}{t.currency}</span>
        </div>
        {deliveryCents > 0 && (
          <div className="checkout-summary-row">
            <span>{t.delivery}</span>
            <span>{(deliveryCents / 100).toFixed(0)}{t.currency}</span>
          </div>
        )}
        <div className="checkout-summary-total">
          {t.total}: {(orderTotalCents / 100).toFixed(0)}{t.currency}
        </div>
      </div>
    </div>
  );
}
